"use client";

import * as React from "react";
import { useFormStatus } from "react-dom";
import { Button } from "@modernatc/ui/button";
import { Callsign } from "@modernatc/ui/callsign";
import { Badge } from "@modernatc/ui/badge";
import { cn } from "@modernatc/ui/cn";
import { tierAtLeast, type SubscriptionTier } from "@modernatc/db";
import { ArrowRight, Check } from "lucide-react";
import {
  FREE_TIER,
  TIERS,
  formatUsd,
  type FreeTierMeta,
  type PricingTier,
  type TierId,
} from "@/lib/pricing";
import { publicEnv } from "@/lib/env";
import { createCheckoutSession } from "@/app/actions/checkout";

interface PricingProps {
  /** "section" renders inside the landing page; "page" is the standalone /pricing route. */
  variant?: "section" | "page";
  /** Pre-error message from the cancel/error redirect. */
  errorMessage?: string;
  /**
   * Current user's effective tier, fetched server-side and passed in.
   * `null` means "not signed in" — every card then shows its acquisition
   * CTA. When set, each card switches between "Current plan",
   * "Included in <higher tier>", and "Upgrade" so the user always
   * knows where they stand.
   */
  currentTier?: SubscriptionTier | null;
}

export function Pricing({
  variant = "section",
  errorMessage,
  currentTier = null,
}: PricingProps) {
  const [billingCycle, setBillingCycle] = React.useState<"monthly" | "yearly">("yearly");

  const trainee = billingCycle === "monthly" ? TIERS.trainee_monthly : TIERS.trainee_yearly;
  const pro = billingCycle === "monthly" ? TIERS.pro_monthly : TIERS.pro_yearly;

  return (
    <section
      id="pricing"
      className={cn(
        variant === "section" && "border-b border-[var(--color-panel-edge)] py-24",
        variant === "page" && "py-16",
      )}
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        {variant === "section" ? (
          <Callsign className="text-xs text-[var(--color-amber)]">§ 04 · Pricing</Callsign>
        ) : (
          <Callsign className="text-xs text-[var(--color-amber)]">Pricing</Callsign>
        )}
        <h2
          className={cn(
            "mt-4 max-w-3xl text-balance font-semibold tracking-tight text-[var(--color-ink)]",
            variant === "section" ? "text-3xl sm:text-4xl" : "text-4xl sm:text-5xl",
          )}
        >
          Priced for a career, not a textbook.
        </h2>
        <p className="mt-4 max-w-2xl text-pretty text-[var(--color-ink-dim)]">
          Start free. Get the real product without a card. Cancel anytime.
        </p>

        {errorMessage ? (
          <div className="mt-6 rounded-[var(--radius)] border border-[var(--color-critical)]/40 bg-[rgb(229_89_52_/_0.08)] px-4 py-3 text-sm text-[var(--color-critical)]">
            {errorMessage}
          </div>
        ) : null}

        <BillingToggle value={billingCycle} onChange={setBillingCycle} />

        <div className="mt-10 grid gap-4 lg:grid-cols-3">
          <FreeTierCard tier={FREE_TIER} currentTier={currentTier} />
          <PaidTierCard tier={trainee} currentTier={currentTier} />
          <PaidTierCard tier={pro} currentTier={currentTier} featured />
        </div>

        <p className="mt-10 text-center text-xs text-[var(--color-ink-muted)]">
          We retired the Lifetime tier in May 2026. Existing Lifetime members
          keep everything, forever, at no further cost.
        </p>
        <p className="mt-3 text-center text-xs text-[var(--color-ink-muted)]">
          Need an institutional license for an AT-CTI program?{" "}
          <a
            className="underline underline-offset-4 hover:text-[var(--color-ink-dim)]"
            href="mailto:partners@modernatc.com"
          >
            partners@modernatc.com
          </a>
          .
        </p>
      </div>
    </section>
  );
}

interface BillingToggleProps {
  value: "monthly" | "yearly";
  onChange: (v: "monthly" | "yearly") => void;
}

function BillingToggle({ value, onChange }: BillingToggleProps) {
  return (
    <div
      role="radiogroup"
      aria-label="Billing cycle"
      className="mt-10 inline-flex items-center rounded-[var(--radius)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-1 text-sm"
    >
      <ToggleOption active={value === "monthly"} onClick={() => onChange("monthly")} label="Monthly" />
      <ToggleOption
        active={value === "yearly"}
        onClick={() => onChange("yearly")}
        label="Yearly"
        suffix="save up to 28%"
      />
    </div>
  );
}

function ToggleOption({
  active,
  onClick,
  label,
  suffix,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  suffix?: string;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onClick}
      className={cn(
        "rounded-[var(--radius-sm)] px-4 py-1.5 text-sm font-medium transition-colors",
        active
          ? "bg-[var(--color-amber)] text-[var(--color-panel-deep)]"
          : "text-[var(--color-ink-dim)] hover:text-[var(--color-ink)]",
      )}
    >
      {label}
      {suffix ? (
        <span
          className={cn(
            "ml-2 text-callsign text-[10px]",
            active ? "text-[var(--color-panel-deep)]/70" : "text-[var(--color-amber)]",
          )}
        >
          {suffix}
        </span>
      ) : null}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Free tier card
// ─────────────────────────────────────────────────────────────────────────────

function FreeTierCard({
  tier,
  currentTier,
}: {
  tier: FreeTierMeta;
  currentTier: SubscriptionTier | null;
}) {
  // "Current plan" only when the user is explicitly on free (signed
  // up, no paid subscription). Higher tiers see "Included" — Free
  // features are a subset of every paid plan, so it's never an upsell
  // target for paid users.
  const isCurrent = currentTier === "free";
  const isIncluded = currentTier != null && currentTier !== "free";

  return (
    <article className="panel-grain relative flex h-full flex-col rounded-[var(--radius-md)] border border-[var(--color-panel-edge)] bg-[var(--color-panel)] p-7">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-[var(--color-ink)]">{tier.name}</h3>
        {isCurrent ? <CurrentBadge /> : null}
      </div>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="text-mono text-5xl font-semibold tracking-tight text-[var(--color-ink)]">
          $0
        </span>
        <span className="text-sm text-[var(--color-ink-dim)]">{tier.intervalLabel}</span>
      </div>

      <ul className="mt-6 space-y-2 text-sm text-[var(--color-ink-dim)]">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span
              aria-hidden
              className="mt-1.5 size-1 shrink-0 rounded-full bg-[var(--color-scope)]"
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-8">
        {isCurrent ? (
          <Button variant="ghost" size="lg" disabled className="w-full">
            <Check className="size-4" />
            You&rsquo;re on Free
          </Button>
        ) : isIncluded ? (
          <Button variant="ghost" size="lg" disabled className="w-full">
            <Check className="size-4" />
            Included in your plan
          </Button>
        ) : (
          <Button asChild variant="secondary" size="lg" className="w-full">
            <a href={`${publicEnv.NEXT_PUBLIC_APP_URL}/signup`}>
              Sign up free
              <ArrowRight />
            </a>
          </Button>
        )}
      </div>
    </article>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Paid tier card (Trainee / Pro)
// ─────────────────────────────────────────────────────────────────────────────

interface PaidTierCardProps {
  tier: PricingTier;
  currentTier: SubscriptionTier | null;
  featured?: boolean;
}

function PaidTierCard({ tier, currentTier, featured }: PaidTierCardProps) {
  const isHighlight = featured ?? tier.highlight ?? false;
  // Tier group is "trainee" or "pro"; map to the entitlement enum value.
  const cardEntitlement: SubscriptionTier = tier.group;

  // Three CTA states keyed off the entitlement layer:
  //   1. Current plan      — the user is exactly on this tier
  //   2. Included          — the user is on a strictly higher tier
  //                          (Pro / Institution / Lifetime grandfathered)
  //   3. Acquisition       — anonymous, on Free, or on a lower tier
  const isCurrent = currentTier === cardEntitlement;
  const isIncluded =
    currentTier != null &&
    currentTier !== cardEntitlement &&
    tierAtLeast(currentTier, cardEntitlement);

  // "Upgrade" copy when the user has an account on a lower paid/free
  // tier; "Get started" when unauthenticated.
  const ctaLabel = currentTier
    ? `Upgrade to ${tier.name}`
    : "Get started";

  return (
    <article
      className={cn(
        "panel-grain relative flex h-full flex-col rounded-[var(--radius-md)]",
        "border bg-[var(--color-panel)] p-7",
        isHighlight
          ? "border-[var(--color-amber)] shadow-[var(--shadow-amber)]"
          : "border-[var(--color-panel-edge)]",
      )}
    >
      {tier.badge ? (
        <Badge variant={isHighlight ? "amber" : "muted"} className="absolute -top-3 left-7">
          {tier.badge}
        </Badge>
      ) : null}

      <div className="flex items-center justify-between gap-2">
        <h3 className="text-lg font-semibold text-[var(--color-ink)]">{tier.name}</h3>
        {isCurrent ? <CurrentBadge /> : null}
      </div>

      <div className="mt-6 flex items-baseline gap-1">
        <span className="text-mono text-5xl font-semibold tracking-tight text-[var(--color-ink)]">
          {formatUsd(tier.priceCents)}
        </span>
        <span className="text-sm text-[var(--color-ink-dim)]">{tier.intervalLabel}</span>
      </div>

      <ul className="mt-6 space-y-2 text-sm text-[var(--color-ink-dim)]">
        {tier.features.map((f) => (
          <li key={f} className="flex items-start gap-2">
            <span
              aria-hidden
              className={cn(
                "mt-1.5 size-1 shrink-0 rounded-full",
                isHighlight ? "bg-[var(--color-amber)]" : "bg-[var(--color-scope)]",
              )}
            />
            <span>{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-8">
        {isCurrent ? (
          <Button variant="ghost" size="lg" disabled className="w-full">
            <Check className="size-4" />
            Current plan
          </Button>
        ) : isIncluded ? (
          <Button variant="ghost" size="lg" disabled className="w-full">
            <Check className="size-4" />
            Included in your plan
          </Button>
        ) : (
          <CheckoutForm
            tierId={tier.id}
            highlight={isHighlight}
            ctaLabel={ctaLabel}
          />
        )}
      </div>
    </article>
  );
}

function CheckoutForm({
  tierId,
  highlight,
  ctaLabel,
}: {
  tierId: TierId;
  highlight: boolean;
  ctaLabel: string;
}) {
  return (
    <form action={createCheckoutSession}>
      <input type="hidden" name="tier" value={tierId} />
      <CheckoutSubmit highlight={highlight} ctaLabel={ctaLabel} />
    </form>
  );
}

function CheckoutSubmit({
  highlight,
  ctaLabel,
}: {
  highlight: boolean;
  ctaLabel: string;
}) {
  const status = useFormStatus();
  return (
    <Button
      type="submit"
      variant={highlight ? "primary" : "secondary"}
      size="lg"
      disabled={status.pending}
      className="w-full"
    >
      {status.pending ? "Opening checkout…" : ctaLabel}
      {!status.pending ? <ArrowRight /> : null}
    </Button>
  );
}

function CurrentBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-[var(--color-clear)]/40 bg-[rgb(95_207_128_/_0.08)] px-2 py-0.5 text-callsign text-[9px] uppercase tracking-wide text-[var(--color-clear)]">
      <span aria-hidden className="size-1 rounded-full bg-[var(--color-clear)]" />
      Current
    </span>
  );
}
